import { useState, useEffect, useMemo } from 'react';
import { Search, Pencil, Plus, X } from 'lucide-react';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import {
  subscribePosCategories, subscribePosProducts,
  addPosCategory, removePosCategory,
} from '../utils/posUtil';
import { POS_ALL_CATEGORIES, POS_SEARCH_MIN_CHARS } from '../constants/posConst';
import PosProductModal from './PosProductModal';
import Modal from './Modal';
import '../css/Pos.css';

/**
 * Inventory — the maintenance screen.
 *
 * Two sections, per the brief: categories (a plain list of strings the admin
 * curates) and products (cards, filtered by category tab and a live search).
 *
 * @returns {JSX.Element}
 */
function PosInventory() {
  const { config } = useConfig();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [activeCategory, setActiveCategory] = useState(POS_ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  // null = closed, 'new' = register, an object = edit that product.
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const stopCategories = subscribePosCategories(setCategories);
    const stopProducts = subscribePosProducts((list) => {
      setProducts(list);
      setLoading(false);
    });
    return () => { stopCategories(); stopProducts(); };
  }, []);

  /**
   * Category tab + search, applied together.
   *
   * Search runs from the FIRST character (POS_SEARCH_MIN_CHARS) across name
   * and description, and is scoped to the active tab rather than replacing
   * it — the tab is where you are, the search narrows it.
   */
  const visibleProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => {
      if (activeCategory !== POS_ALL_CATEGORIES && product.category !== activeCategory) return false;
      if (term.length < POS_SEARCH_MIN_CHARS) return true;
      return `${product.name || ''} ${product.description || ''}`.toLowerCase().includes(term);
    });
  }, [products, activeCategory, search]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await addPosCategory(newCategory);
      setNewCategory('');
    } catch (error) {
      setModal({ title: 'Could not add category', message: error.message, type: 'error' });
    }
  };

  const handleRemoveCategory = (name) => {
    const inUse = products.filter(p => p.category === name).length;
    setModal({
      title: `Remove "${name}"?`,
      // Says what happens to the products rather than leaving it to be
      // discovered: nothing is deleted, they simply lose this tab.
      message: inUse > 0
        ? `${inUse} product${inUse === 1 ? '' : 's'} use this category. They are not deleted — they keep the category name and stay visible under All.`
        : 'No products use this category.',
      type: 'warning',
      confirmText: 'Remove',
      showCancel: true,
      cancelText: 'Keep it',
      onConfirm: async () => {
        setModal(null);
        try {
          await removePosCategory(name);
          if (activeCategory === name) setActiveCategory(POS_ALL_CATEGORIES);
        } catch (error) {
          setModal({ title: 'Error', message: error.message, type: 'error' });
        }
      },
    });
  };

  if (loading) {
    return <div className="pos-loading">{spinnerRing('Loading inventory…')}</div>;
  }

  return (
    <div className="pos-inventory">
      {/* ---------- Section 1: categories ---------- */}
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Categories</h2>
          <p>Used to group products and to build the filter tabs below.</p>
        </div>

        <form className="pos-category-form" onSubmit={handleAddCategory}>
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="e.g. Drinks"
            aria-label="New category name"
          />
          <button type="submit" className="pos-btn pos-btn-primary" disabled={!newCategory.trim()}>
            <Plus size={15} /> Add
          </button>
        </form>

        {categories.length === 0 ? (
          <p className="pos-empty-line">No categories yet.</p>
        ) : (
          <ul className="pos-category-list">
            {categories.map((name) => (
              <li key={name} className="pos-category-chip">
                <span>{name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${name}`}
                  title={`Remove ${name}`}
                  onClick={() => handleRemoveCategory(name)}
                >
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---------- Section 2: products ---------- */}
      <section className="pos-section">
        <div className="pos-section-head pos-section-head-row">
          <div>
            <h2>Products</h2>
            <p>{products.length} registered.</p>
          </div>
          <button type="button" className="pos-btn pos-btn-primary" onClick={() => setEditing('new')}>
            <Plus size={15} /> Add new product
          </button>
        </div>

        <div className="pos-filter-bar">
          <div className="pos-cat-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeCategory === POS_ALL_CATEGORIES}
              className={`pos-cat-tab${activeCategory === POS_ALL_CATEGORIES ? ' active' : ''}`}
              onClick={() => setActiveCategory(POS_ALL_CATEGORIES)}
            >
              All
            </button>
            {categories.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={activeCategory === name}
                className={`pos-cat-tab${activeCategory === name ? ' active' : ''}`}
                onClick={() => setActiveCategory(name)}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="pos-search">
            <Search size={15} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or description"
              aria-label="Search products"
            />
          </div>
        </div>

        {visibleProducts.length === 0 ? (
          <p className="pos-empty-line">
            {products.length === 0
              ? 'No products yet. Use "Add new product" to register the first one.'
              : 'Nothing matches this filter.'}
          </p>
        ) : (
          <div className="pos-product-grid">
            {visibleProducts.map((product) => (
              <article key={product.id} className="pos-product-card">
                <div className="pos-product-media">
                  {product.imageUrl
                    ? <img src={product.imageUrl} alt="" />
                    : <span className="pos-product-noimage" aria-hidden="true">No image</span>}
                </div>
                <div className="pos-product-body">
                  <h3 className="pos-product-name">{product.name}</h3>
                  {product.category && <span className="pos-product-cat">{product.category}</span>}
                  {product.description && <p className="pos-product-desc">{product.description}</p>}
                  <div className="pos-product-foot">
                    <span className="pos-product-price">{formatMoney(Number(product.price || 0), config)}</span>
                    {/* Reads as a warning at zero: an out-of-stock item is
                        the one number worth noticing on this card. */}
                    <span className={`pos-product-stock${Number(product.supplies) <= 0 ? ' out' : ''}`}>
                      {Number(product.supplies) || 0} in stock
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="pos-product-edit"
                  onClick={() => setEditing(product)}
                  aria-label={`Edit ${product.name}`}
                >
                  <Pencil size={14} /> Edit
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {editing && (
        <PosProductModal
          product={editing === 'new' ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
        />
      )}

      <Modal
        isOpen={!!modal}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText={modal?.confirmText || 'OK'}
        showCancel={modal?.showCancel}
        cancelText={modal?.cancelText}
        onConfirm={modal?.onConfirm}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosInventory;
